/**
 * Contenu éditorial de la page /anniversaire (anniversaire & fêtes de famille).
 * Texte fourni par le client (fichier 06) — variables renseignées par le client.
 */

export const ANNIVERSAIRE_CONTENT = {
  fr: {
    meta: {
      title: "Anniversaire & fête de famille à Ville-d'Avray",
      description:
        "Fêtez un anniversaire, des fiançailles ou un départ en retraite dans un hôtel particulier à Ville-d'Avray (92) : privatisation dès 12 convives, terrasse de 240 m², cuisine maison.",
      keywords:
        "anniversaire Ville d'Avray, privatisation restaurant 92, fête de famille Hauts-de-Seine, fiançailles, départ en retraite",
    },
    breadcrumb: [
      { label: "Accueil", to: "/" },
      { label: "Événements", to: "/evenements" },
      { label: "Anniversaire" },
    ],
    overline: "Anniversaires & fêtes de famille · Ville-d'Avray (92)",
    h1: "Fêter un anniversaire dans un hôtel particulier à Ville-d'Avray",
    intro:
      "30, 40, 50 ou 60 ans, fiançailles, noces d'or, départ en retraite : L'Entracte privatise une salle, la terrasse ou tout l'établissement pour votre fête, de 12 à 100 convives. Vous choisissez le format, le chef compose le menu, notre équipe s'occupe du reste.",
    ctaDevis: "Demander un devis",
    ctaCall: "Appeler le 01 82 00 18 67",
    heroAlt: "Dîner d'anniversaire privatisé à L'Entracte, Ville-d'Avray",

    brief: {
      title: "Votre fête en bref",
      caption: "Informations clés pour un anniversaire à L'Entracte",
      rows: [
        ["Lieu", "Hôtel particulier, 3 rue de Versailles, 92410 Ville-d'Avray"],
        ["Capacité", "De 12 à 100 invités assis, jusqu'à 130 en cocktail"],
        ["Formats", "Dîner assis, cocktail dînatoire, déjeuner en terrasse"],
        [
          "Budget indicatif",
          "Dîner assis dès 50 € HT par personne, cocktail dînatoire dès 70 € HT (à partir de 15 invités)",
        ],
        ["Durée", "3 heures incluses, heures supplémentaires 400 € de l'heure"],
        ["Parking", "Stationnement à proximité immédiate du restaurant"],
      ],
    },

    occasions: {
      title: "Toutes les occasions de se retrouver",
      items: [
        { label: "Anniversaires", body: ": les décennies qui se fêtent en grand, ou un anniversaire surprise entre proches." },
        { label: "Fiançailles", body: ": une première réception pour réunir les deux familles avant le mariage." },
        { label: "Anniversaires de mariage et noces", body: ": noces d'argent, d'or ou de diamant, dans un cadre à la hauteur." },
        { label: "Départs en retraite et pots de départ", body: ": entre collègues, en terrasse ou en salle." },
        { label: "Retrouvailles et fêtes de famille", body: ": cousinades, fêtes de fin d'études, grandes tablées." },
      ],
      weddingLinkLabel: "Organiser un mariage à L'Entracte",
    },

    formats: {
      title: "Choisissez votre format",
      items: [
        {
          name: "Le dîner assis",
          body: "Un menu en plusieurs services composé avec le chef, dans l'une de nos salles ou en privatisation totale.",
          alt: "Dîner privatisé dans le Grand Salon de L'Entracte",
        },
        {
          name: "Le cocktail dînatoire",
          body: "Pièces salées et sucrées préparées par notre cuisine : idéal pour circuler, discuter et, selon votre projet, danser.",
          alt: "Cocktail dînatoire sur la terrasse de L'Entracte",
        },
        {
          name: "Le déjeuner en terrasse",
          body: "Aux beaux jours, la terrasse de 240 m² sur parc devient votre salle de réception à ciel ouvert.",
          alt: "Déjeuner de fête sur la terrasse de 240 m² de L'Entracte",
        },
      ],
    },

    included: {
      title: "Ce qui est compris",
      items: [
        "La privatisation de l'espace choisi.",
        "Le menu sur-mesure du chef, 100 % fait maison, et une sélection de vins.",
        "Le service, coordonné par notre équipe.",
        "L'aménagement scénographique de la salle et de la terrasse.",
      ],
      rows: [
        [
          "Le gâteau",
          "Le chef peut réaliser votre dessert de fête, et vous pouvez aussi apporter votre propre gâteau (conditions précisées dans le devis).",
        ],
        [
          "La musique",
          "Musique d'ambiance possible ; le lieu n'a pas de piste de danse dédiée et la fête se termine au plus tard à minuit.",
        ],
      ],
      caption: "Précisions sur le gâteau et la musique",
    },

    small: {
      title: "Moins de 12 personnes ?",
      body1: "Pour une fête en petit comité, pas besoin de privatiser :",
      link1Label: "réservez simplement une table",
      link1: "/reservation",
      body2: "et signalez l'occasion dans votre réservation. Vous pouvez aussi offrir un dîner à L'Entracte avec notre",
      link2Label: "carte cadeau",
      link2: "/carte-cadeau",
    },

    access: {
      title: "Accès",
      intro:
        "L'Entracte est au 3 rue de Versailles, 92410 Ville-d'Avray : 15 minutes de Versailles, 15 minutes de Paris, tout près de Sèvres, Chaville et Saint-Cloud.",
      note:
        "Gare de Sèvres – Ville-d'Avray à 1 km, desservie par la ligne L (depuis Paris Saint-Lazare et La Défense) et la ligne U (depuis La Défense). Parking : stationnement à proximité immédiate du restaurant.",
    },

    faqTitle: "Questions fréquentes",
    faq: [
      {
        question: "Combien d'invités pouvez-vous accueillir pour un anniversaire ?",
        answer:
          "De 12 à 100 invités assis, et jusqu'à 130 en cocktail dînatoire en privatisation totale.",
      },
      {
        question: "Quel budget prévoir ?",
        answer:
          "Dîner assis dès 50 € HT par personne, cocktail dînatoire dès 70 € HT (à partir de 15 invités), selon le format, le menu et le nombre d'invités. Chaque devis est établi sur-mesure.",
      },
      {
        question: "Peut-on apporter notre gâteau d'anniversaire ?",
        answer:
          "Le chef peut réaliser votre dessert de fête, et vous pouvez aussi apporter votre propre gâteau (conditions précisées dans le devis).",
      },
      {
        question: "Peut-on mettre de la musique et danser ?",
        answer:
          "Musique d'ambiance possible ; le lieu n'a pas de piste de danse dédiée et la fête se termine au plus tard à minuit.",
      },
      {
        question: "Peut-on décorer la salle ?",
        answer:
          "Notre équipe prend en charge l'aménagement scénographique de la salle et de la terrasse. Parlez-nous de vos envies lors de votre demande de devis.",
      },
      {
        question: "Peut-on organiser un anniversaire surprise ?",
        answer:
          "Oui. Nous organisons l'arrivée des invités avant celle de la personne fêtée ; précisez-le dans votre demande.",
      },
      {
        question: "Combien de temps à l'avance faut-il réserver ?",
        answer: "Comptez environ 2 mois à l'avance.",
      },
    ],

    devis: {
      title: "Parlons de votre fête",
      body: "Indiquez-nous l'occasion, la date et le nombre d'invités : nous revenons vers vous avec une proposition.",
      contact: "Téléphone : 01 82 00 18 67 · E-mail : restaurantlentractevda@gmail.com",
    },

    related: {
      title: "Autres événements",
      links: [
        { to: "/evenements", label: "Toutes nos réceptions privées" },
        { to: "/mariage", label: "Mariage" },
        { to: "/bapteme", label: "Baptême et communion" },
        { to: "/menu", label: "La carte du restaurant" },
      ],
    },

    eventType: "Anniversaire / fête de famille",
  },

  en: {
    meta: {
      title: "Birthdays & family celebrations in Ville-d'Avray",
      description:
        "Celebrate a birthday, an engagement or a retirement in a private townhouse in Ville-d'Avray (92): venue hire from 12 guests, a 240 m² terrace, house-made cuisine.",
      keywords:
        "birthday Ville d'Avray, restaurant hire 92, family celebration Hauts-de-Seine, engagement party, retirement party",
    },
    breadcrumb: [
      { label: "Home", to: "/" },
      { label: "Events", to: "/evenements" },
      { label: "Birthday" },
    ],
    overline: "Birthdays & family celebrations · Ville-d'Avray (92)",
    h1: "Celebrate a birthday in a private townhouse in Ville-d'Avray",
    intro:
      "30th, 40th, 50th or 60th birthdays, engagements, golden anniversaries, retirement parties: L'Entracte hires out a lounge, the terrace or the whole venue for your celebration, for 12 to 100 guests. You choose the format, the chef creates the menu, our team takes care of the rest.",
    ctaDevis: "Request a quote",
    ctaCall: "Call +33 1 82 00 18 67",
    heroAlt: "Private birthday dinner at L'Entracte, Ville-d'Avray",

    brief: {
      title: "Your celebration at a glance",
      caption: "Key information for a birthday at L'Entracte",
      rows: [
        ["Venue", "Private townhouse, 3 rue de Versailles, 92410 Ville-d'Avray"],
        ["Capacity", "12 to 100 seated guests, up to 130 for a cocktail reception"],
        ["Formats", "Seated dinner, standing dinner cocktail, lunch on the terrace"],
        [
          "Indicative budget",
          "Seated dinner from €50 excl. VAT per person, standing dinner cocktail from €70 excl. VAT (from 15 guests)",
        ],
        ["Duration", "3 hours included, additional hours €400 per hour"],
        ["Parking", "In the immediate vicinity of the restaurant"],
      ],
    },

    occasions: {
      title: "Every reason to get together",
      items: [
        { label: "Birthdays", body: ": milestone decades celebrated in style, or a surprise birthday among close friends." },
        { label: "Engagements", body: ": a first reception to bring both families together before the wedding." },
        { label: "Wedding anniversaries", body: ": silver, golden or diamond anniversaries, in a setting to match." },
        { label: "Retirements and farewell parties", body: ": among colleagues, on the terrace or indoors." },
        { label: "Reunions and family celebrations", body: ": family reunions, graduation parties, large tables." },
      ],
      weddingLinkLabel: "Plan a wedding at L'Entracte",
    },

    formats: {
      title: "Choose your format",
      items: [
        {
          name: "The seated dinner",
          body: "A multi-course menu created with the chef, in one of our lounges or with full venue hire.",
          alt: "Private dinner in the Grand Salon of L'Entracte",
        },
        {
          name: "The standing dinner cocktail",
          body: "Savoury and sweet canapés prepared by our kitchen: ideal for mingling, chatting and, depending on your plans, dancing.",
          alt: "Standing dinner cocktail on the terrace of L'Entracte",
        },
        {
          name: "Lunch on the terrace",
          body: "In fine weather, the 240 m² terrace overlooking the park becomes your open-air reception room.",
          alt: "Celebration lunch on L'Entracte's 240 m² terrace",
        },
      ],
    },

    included: {
      title: "What is included",
      items: [
        "Private hire of the chosen space.",
        "The chef's bespoke menu, 100% house-made, and a selection of wines.",
        "The service, coordinated by our team.",
        "The staging and set-up of the room and the terrace.",
      ],
      rows: [
        [
          "The cake",
          "The chef can create your celebration dessert, and you may also bring your own cake (conditions set out in the quote).",
        ],
        [
          "Music",
          "Background music is possible; the venue has no dedicated dance floor and the celebration ends by midnight at the latest.",
        ],
      ],
      caption: "Details about the cake and the music",
    },

    small: {
      title: "Fewer than 12 people?",
      body1: "For a small celebration there is no need for private hire:",
      link1Label: "simply book a table",
      link1: "/reservation",
      body2: "and mention the occasion in your booking. You can also give a dinner at L'Entracte with our",
      link2Label: "gift card",
      link2: "/carte-cadeau",
    },

    access: {
      title: "Getting here",
      intro:
        "L'Entracte is at 3 rue de Versailles, 92410 Ville-d'Avray: 15 minutes from Versailles, 15 minutes from Paris, very close to Sèvres, Chaville and Saint-Cloud.",
      note:
        "Sèvres – Ville-d'Avray station 1 km away, served by line L (from Paris Saint-Lazare and La Défense) and line U (from La Défense). Parking: in the immediate vicinity of the restaurant.",
    },

    faqTitle: "Frequently asked questions",
    faq: [
      {
        question: "How many guests can you host for a birthday?",
        answer:
          "From 12 to 100 seated guests, and up to 130 for a standing dinner cocktail with full venue hire.",
      },
      {
        question: "What budget should we plan for?",
        answer:
          "Seated dinner from €50 excl. VAT per person, standing dinner cocktail from €70 excl. VAT (from 15 guests), depending on the format, the menu and the number of guests. Every quote is bespoke.",
      },
      {
        question: "Can we bring our own birthday cake?",
        answer:
          "The chef can create your celebration dessert, and you may also bring your own cake (conditions set out in the quote).",
      },
      {
        question: "Can we play music and dance?",
        answer:
          "Background music is possible; the venue has no dedicated dance floor and the celebration ends by midnight at the latest.",
      },
      {
        question: "Can we decorate the room?",
        answer:
          "Our team takes care of the staging and set-up of the room and the terrace. Tell us what you have in mind in your quote request.",
      },
      {
        question: "Can we organise a surprise birthday?",
        answer:
          "Yes. We arrange for the guests to arrive before the guest of honour; mention it in your request.",
      },
      {
        question: "How far in advance should we book?",
        answer: "Allow around 2 months in advance.",
      },
    ],

    devis: {
      title: "Let's talk about your celebration",
      body: "Tell us the occasion, the date and the number of guests: we will get back to you with a proposal.",
      contact: "Phone: +33 1 82 00 18 67 · Email: restaurantlentractevda@gmail.com",
    },

    related: {
      title: "Other events",
      links: [
        { to: "/evenements", label: "All our private receptions" },
        { to: "/mariage", label: "Wedding" },
        { to: "/bapteme", label: "Christening and communion" },
        { to: "/menu", label: "The restaurant menu" },
      ],
    },

    eventType: "Birthday / family celebration",
  },
};
